import { IdToken, useAuth0 } from "@auth0/auth0-react";
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useChat } from "ai/react";
import { useInterval } from "@/hooks/useInterval";
import { params } from "@/params";
import dayjs from "dayjs";
import { ExploreInteractionType } from "@/v2/entities/explore/Explore.model";
import { Filter } from "@/v2/entities/explore/Explore.filter";
import { FeatureAccessContext } from "@/context/featureAccess";

import {
  AskAnythingChatContextType,
  AskAnythingCombinedContextType,
  AskAnythingSessionContextType,
  ChatSession,
  MAX_SESSION_NAME_LENGTH,
} from "./AskAnythingChat.model";

const AskAnythingSessionContext = createContext<
  AskAnythingSessionContextType | undefined
>(undefined);
const AskAnythingChatContext = createContext<
  AskAnythingChatContextType | undefined
>(undefined);

interface AskAnythingSessionProviderProps {
  children: React.ReactNode;
  source: ExploreInteractionType;
}

export function AskAnythingSessionProvider({
  children,
  source,
}: AskAnythingSessionProviderProps) {
  // Store sessions and active IDs by source
  const [sessionsBySource, setSessionsBySource] = useState<
    Record<string, ChatSession[]>
  >({});
  const [activeIdsBySource, setActiveIdsBySource] = useState<
    Record<string, string>
  >({});

  // Helper to get initial default session
  const createDefaultSession = useCallback(() => {
    return {
      id: String(dayjs().valueOf()),
      title: "New Chat",
      messages: [],
      s3FolderUrl: null,
      chatId: null,
      savedScrollPosition: null,
      createdAt: dayjs().valueOf(),
    };
  }, []);

  // Ensure initialization for the current source
  useEffect(() => {
    setSessionsBySource((prev) => {
      if (!prev[source] || prev[source].length === 0) {
        return {
          ...prev,
          [source]: [createDefaultSession()],
        };
      }
      return prev;
    });

    setActiveIdsBySource((prev) => {
      if (!prev[source]) {
        return {
          ...prev,
        };
      }
      return prev;
    });
  }, [source, createDefaultSession]);

  // Derived state for current source
  // If sessions for source don't exist yet, provide a temporary default (it will be overwritten by useEffect)
  const sessions = useMemo(() => {
    if (sessionsBySource[source] && sessionsBySource[source].length > 0) {
      return sessionsBySource[source];
    }
    // Fallback while effect runs or if empty
    return [createDefaultSession()];
  }, [sessionsBySource, source, createDefaultSession]);

  const activeSessionId = useMemo(() => {
    if (activeIdsBySource[source]) {
      return activeIdsBySource[source];
    }
    // Fallback to first session
    return sessions[0]?.id;
  }, [activeIdsBySource, source, sessions]);

  const createNewSession = useCallback(() => {
    // 1. Check if current session is empty
    const currentSession = sessions.find((s) => s.id === activeSessionId);
    if (currentSession && currentSession.messages.length === 0) {
      return; // Already on an empty chat, do nothing
    }

    // 2. Check if there is any other empty session
    const existingEmptySession = sessions.find((s) => s.messages.length === 0);
    if (existingEmptySession) {
      setActiveIdsBySource((prev) => ({
        ...prev,
        [source]: existingEmptySession.id,
      }));
      return;
    }

    // 3. Create new session
    const newId = String(dayjs().valueOf());
    const newSession: ChatSession = {
      id: newId,
      title: "New Chat",
      messages: [],
      s3FolderUrl: null,
      chatId: null,
      savedScrollPosition: null,
      createdAt: dayjs().valueOf(),
    };

    setSessionsBySource((prev) => ({
      ...prev,
      [source]: [newSession, ...(prev[source] || [])],
    }));
    setActiveIdsBySource((prev) => ({
      ...prev,
      [source]: newId,
    }));
  }, [sessions, activeSessionId, source]);

  const switchSession = useCallback(
    (sessionId: string) => {
      setActiveIdsBySource((prev) => ({
        ...prev,
        [source]: sessionId,
      }));
    },
    [source]
  );

  const updateSession = useCallback(
    (id: string, updates: Partial<ChatSession>) => {
      setSessionsBySource((prev) => ({
        ...prev,
        [source]: (prev[source] || []).map((s) =>
          s.id === id ? { ...s, ...updates } : s
        ),
      }));
    },
    [source]
  );

  const value = useMemo(
    () => ({
      sessions,
      activeSessionId,
      createNewSession,
      switchSession,
      updateSession,
    }),
    [sessions, activeSessionId, createNewSession, switchSession, updateSession]
  );

  return (
    <AskAnythingSessionContext.Provider value={value}>
      {children}
    </AskAnythingSessionContext.Provider>
  );
}

interface AskAnythingChatSessionProps {
  source: ExploreInteractionType;
  filter: Filter;
  children: React.ReactNode;
}

export function AskAnythingChatSession({
  source,
  filter,
  children,
}: AskAnythingChatSessionProps) {
  const { activeSessionId, sessions, updateSession } = useAskAnythingSession();

  // Find the active session data from the list
  const activeSession = useMemo(
    () => sessions.find((s) => s.id === activeSessionId) || sessions[0],
    [sessions, activeSessionId]
  );

  const [tokenId, setToken] = useState<IdToken | undefined>(undefined);
  const { isAuthenticated, getIdTokenClaims } = useAuth0();
  const { selectedWorkspace } = useContext(FeatureAccessContext);

  const conversationIdRef = useRef<string | null>(activeSession.chatId);
  const [chatId, setChatId] = useState<string | null>(activeSession.chatId);
  const [s3FolderUrl, setS3FolderUrl] = useState<string | null>(
    activeSession.s3FolderUrl
  );
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [loadingText, setLoadingText] = useState<string>("Thinking...");
  const [savedScrollPosition, setSavedScrollPosition] = useState<number | null>(
    activeSession.savedScrollPosition
  );

  useEffect(() => {
    (async () => {
      const token = await getIdTokenClaims();
      setToken(token);
    })();
  }, [isAuthenticated, getIdTokenClaims]);

  useInterval(() => {
    (async () => {
      if (isAuthenticated) {
        const token = await getIdTokenClaims();
        setToken(token);
      }
    })();
  }, 1000 * 60 * 10); // every 10 minutes

  const authToken = tokenId?.__raw ? tokenId.__raw : "";
  const headers = useMemo<Record<string, string>>(() => {
    const h: Record<string, string> = {
      Authorization: `Bearer ${authToken}`,
    };
    if (selectedWorkspace) {
      h["x-tenant-id"] = selectedWorkspace;
    }
    return h;
  }, [authToken, selectedWorkspace]);

  const api = useMemo(() => {
    return `${params.API_URL}/platform/v2/explore/ask-anything`;
  }, []);

  const filterString = useMemo(() => {
    return filter ? JSON.stringify(filter) : undefined;
  }, [filter]);

  const baseBodyData = useMemo(
    () => ({
      tenantId: selectedWorkspace,
      source,
      filterString,
      s3FolderUrl: s3FolderUrl ?? undefined,
    }),
    [selectedWorkspace, source, filterString, s3FolderUrl]
  );

  const customFetch = useCallback(
    async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      let modifiedInit = init;

      if (init?.body && typeof init.body === "string") {
        try {
          const body = JSON.parse(init.body);
          const { id, messages: rawMessages, ...restBody } = body;

          // Transform messages: only keep role & content, exclude the last user message
          let cleanedMessages: Array<{ role: string; content: string }> = [];
          if (Array.isArray(rawMessages) && rawMessages.length > 0) {
            // Exclude the last message if it's from the user (it's in the 'question' field)
            const messagesToInclude =
              rawMessages[rawMessages.length - 1]?.role === "user"
                ? rawMessages.slice(0, -1)
                : rawMessages;

            cleanedMessages = messagesToInclude.map(
              (msg: { role: string; content: string }) => ({
                role: msg.role,
                content: msg.content,
              })
            );
          }

          const modifiedBody = {
            ...restBody,
            messages: cleanedMessages,
          };

          modifiedInit = {
            ...init,
            body: JSON.stringify(modifiedBody),
          };
        } catch {
          // If parsing fails, use original init
        }
      }

      const response = await fetch(input, modifiedInit);
      const reader = response.body?.getReader();
      if (!reader) return response;

      const stream = new ReadableStream({
        async start(controller) {
          const decoder = new TextDecoder();
          const encoder = new TextEncoder();
          let buffer = "";

          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;

              const chunk = decoder.decode(value, { stream: true });
              const parts = (buffer + chunk).split("\n");
              buffer = parts.pop() || "";

              for (const part of parts) {
                const trimmed = part.trim();
                if (trimmed.startsWith("f:")) {
                  try {
                    const json = JSON.parse(trimmed.slice(2));
                    if (json.state === "load_data") {
                      setLoadingText(
                        source === "tickets"
                          ? "Loading your tickets..."
                          : "Loading your conversations..."
                      );
                      continue;
                    } else if (json.state === "analyze") {
                      setLoadingText("Analyzing...");
                      continue;
                    }
                  } catch {
                    // swallow JSON errors, just process as normal chunk below
                  }
                }
                if (trimmed !== "") {
                  controller.enqueue(encoder.encode(part + "\n"));
                }
              }
            }
            if (buffer) {
              if (buffer.trim().startsWith('f:{"state":"load_data"}')) {
                setLoadingText("Loading data...");
              } else {
                controller.enqueue(encoder.encode(buffer));
              }
            }
            controller.close();
          } catch (e) {
            controller.error(e);
          }
        },
        cancel(reason) {
          reader.cancel(reason);
        },
      });

      return new Response(stream, {
        headers: response.headers,
        status: response.status,
        statusText: response.statusText,
      });
    },
    [source]
  );

  const { messages, input, setInput, append, reload, setMessages, status } =
    useChat({
      id: activeSessionId,
      api,
      headers,
      fetch: customFetch,
      sendExtraMessageFields: true,
      body: baseBodyData,
      initialMessages: activeSession.messages,
      onResponse: (response: Response) => {
        const newChatId = response.headers.get("x-chat-id") ?? null;
        if (newChatId) {
          conversationIdRef.current = newChatId;
          setChatId(newChatId);
        }

        const newS3FolderUrl = response.headers.get("x-s3-folder-url") ?? null;
        if (newS3FolderUrl) {
          setS3FolderUrl(newS3FolderUrl);
        }
      },
      onError: (error: Error) => {
        console.error("AskAnything chat error:", error);
      },
    });

  // Sync state back to session storage
  useEffect(() => {
    let title = activeSession.title;

    // Only update title if it's "New Chat" and we have a user message
    if (title === "New Chat") {
      const firstUserMessage = messages.find((m) => m.role === "user");
      if (firstUserMessage) {
        const content = firstUserMessage.content.trim();
        if (content.length <= MAX_SESSION_NAME_LENGTH) {
          title = content;
        } else {
          title = `${content.slice(0, MAX_SESSION_NAME_LENGTH)}...`;
        }
      }
    }

    updateSession(activeSessionId, {
      messages,
      s3FolderUrl,
      chatId,
      savedScrollPosition,
      title,
      lastUpdatedAt: dayjs().valueOf(),
    } as Partial<ChatSession>); // Using type assertion to include lastUpdatedAt if it's not in interface yet or merge is partial
  }, [
    messages,
    s3FolderUrl,
    chatId,
    savedScrollPosition,
    activeSessionId,
    updateSession,
    // activeSession.title // intentionally omitted to avoid loop if title updates? No, title change should trigger.
  ]);

  const handleSubmit = useCallback(
    (e?: React.FormEvent<HTMLFormElement>) => {
      if (e) e.preventDefault();
      if (!input.trim() || !selectedWorkspace) {
        if (!selectedWorkspace) {
          console.error("AskAnything: Cannot submit - missing workspace");
        }
        return;
      }

      const currentQuestion = input.trim();
      setInput("");
      setLoadingText("Thinking...");

      // Use append with body override to include the question field
      append(
        {
          role: "user",
          content: currentQuestion,
          createdAt: new Date(),
        },
        {
          body: {
            ...baseBodyData,
            question: currentQuestion,
            s3FolderUrl: s3FolderUrl ?? undefined,
          },
        }
      );
    },
    [input, selectedWorkspace, baseBodyData, append, setInput, s3FolderUrl]
  );

  const reset = useCallback(() => {
    setMessages([]);
    setS3FolderUrl(null);
    setChatId(null);
    conversationIdRef.current = null;
    setSavedScrollPosition(null);
  }, [setMessages]);

  const saveScrollPosition = useCallback((position: number) => {
    setSavedScrollPosition(position);
  }, []);

  const value = useMemo(
    () => ({
      messages,
      input,
      handleSubmit,
      append,
      reload,
      setMessages,
      status,
      setInput,
      reset,
      chatId,
      isDisabled,
      setIsDisabled,
      loadingText,
      savedScrollPosition,
      saveScrollPosition,
    }),
    [
      messages,
      input,
      handleSubmit,
      append,
      reload,
      setMessages,
      status,
      setInput,
      reset,
      chatId,
      isDisabled,
      setIsDisabled,
      loadingText,
      savedScrollPosition,
      saveScrollPosition,
    ]
  );

  return (
    <AskAnythingChatContext.Provider value={value}>
      {children}
    </AskAnythingChatContext.Provider>
  );
}

function useAskAnythingSession(): AskAnythingSessionContextType {
  const context = useContext(AskAnythingSessionContext);
  if (context === undefined) {
    throw new Error(
      "useAskAnythingSession must be used within an AskAnythingSessionProvider"
    );
  }
  return context;
}

export function useAskAnythingChat(): AskAnythingCombinedContextType {
  const sessionContext = useContext(AskAnythingSessionContext);
  const chatContext = useContext(AskAnythingChatContext);

  if (sessionContext === undefined) {
    throw new Error(
      "useAskAnythingChat must be used within an AskAnythingSessionProvider"
    );
  }

  // If chat context is missing, return session context alone
  // This allows components that only rely on session data to continue working
  if (chatContext === undefined) {
    return sessionContext;
  }

  // Merge both contexts when chat context is available
  return {
    ...sessionContext,
    ...chatContext,
  };
}

// Re-export for backward compatibility if needed, but we recommend using Providers directly
export const AskAnythingChatWrapper = AskAnythingSessionProvider;
